import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, quantitySchema, rateSchema, timestampSchema, unitPriceSchema, uuidSchema, } from "../common/primitives.js";
import { businessUnitTypeSchema } from "../auth/features.js";
import { POS_PIN_MAX_LENGTH, POS_PIN_MIN_LENGTH, posLineKindSchema, posSaleKindSchema, posSaleSourceSchema, posSaleStatusSchema, posSaleWarningSchema, posTenderTypeSchema, } from "./enums.js";
/**
 * Yerel kasa satışlarının YÖNETİM (bulut) uçları: liste, ayrıntı, gün / kasiyer raporu ve personel PIN'i.
 * Satışın KENDİSİ buradan girilmez — kasa satışı yalnızca ajan senkronundan (`PosSale`) gelir.
 */
export const posSaleVatLineSchema = z.object({
    vatRateCode: z.string(),
    ratePercent: rateSchema,
    base: moneySchema.describe("Matrah (kuruş)"),
    vat: moneySchema,
    gross: moneySchema.describe("KDV dâhil"),
});
/** Liste satırı: fişin başlığı. Satır ve ödeme ayrıntısı AYRINTI ucundadır. */
export const posSaleSummarySchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string().nullable(),
    agentSaleId: z.string(),
    occurredAt: timestampSchema,
    businessDate: localDateSchema,
    shiftId: uuidSchema.nullable(),
    shiftNo: z.string().nullable(),
    staffId: uuidSchema.nullable(),
    staffName: z.string().nullable().describe("KVKK: personel adı"),
    kind: posSaleKindSchema,
    status: posSaleStatusSchema,
    gross: moneySchema,
    discountTotal: moneySchema,
    net: moneySchema,
    vatTotal: moneySchema,
    receiptRef: z.string().nullable(),
    fiscalDeviceSerial: z.string().nullable(),
    zNo: z.number().int().nullable(),
    receiptNo: z.number().int().nullable(),
    counterpartyId: uuidSchema.nullable(),
    counterpartyName: z.string().nullable(),
    afterDayLock: z.boolean(),
    warnings: z.array(z.string()),
    source: posSaleSourceSchema,
    lineCount: z.number().int().nonnegative(),
    tenderTypes: z.array(posTenderTypeSchema),
    createdAt: timestampSchema,
});
export const posSalePageSchema = pageOf(posSaleSummarySchema);
export const posSaleLineSchema = z.object({
    lineNo: z.number().int(),
    kind: posLineKindSchema,
    productId: uuidSchema.nullable(),
    productCode: z.string().nullable(),
    productName: z.string().nullable(),
    description: z.string().nullable(),
    barcode: z.string().nullable(),
    quantity: quantitySchema,
    unitPrice: unitPriceSchema.describe("Birim fiyat (₺, KDV dâhil) — satır türünden bağımsız olarak her zaman ₺"),
    gross: moneySchema,
    discountAmount: moneySchema,
    discountCampaignId: uuidSchema.nullable(),
    discountCampaignName: z.string().nullable(),
    discountReason: z.string().nullable(),
    net: moneySchema,
    vatRateCode: z.string(),
    vatRatePercent: rateSchema,
    vatAmount: moneySchema,
    businessUnit: businessUnitTypeSchema.nullable(),
    accountingGroup: z.string().nullable(),
    fuelAgentTxId: z.string().nullable(),
    isAgeRestricted: z.boolean(),
    /** Prompt 17: satır abonelik hakkıyla sıfırlandıysa aboneliği (kiracıda doğrulanmış). */
    subscriptionId: uuidSchema.nullable(),
    /** Prompt 19: şarj oturumu ödemesi (bağ EV tüketicisinde doğrulanır; FK yok). */
    evSessionId: uuidSchema.nullable(),
});
export const posSaleTenderSchema = z.object({
    tenderNo: z.number().int(),
    tenderType: posTenderTypeSchema,
    amount: moneySchema,
    changeGiven: moneySchema,
    posTerminalId: uuidSchema.nullable(),
    posTerminalName: z.string().nullable(),
    slipNo: z.string().nullable(),
    counterpartyId: uuidSchema.nullable(),
    counterpartyName: z.string().nullable(),
    /** Ajanın SATIŞ ANINDA (önbellek limitiyle) verdiği karar; sunucunun gerçek denetimi ayrıdır. */
    creditCheck: z
        .object({
        verdict: z.string().nullable(),
        source: z.string(),
        availableBefore: moneySchema.nullable(),
        availableAfter: moneySchema.nullable(),
        toleranceUsed: moneySchema.nullable(),
        overridden: z.boolean(),
    })
        .nullable(),
    shiftCreditSaleId: uuidSchema.nullable(),
    /** Prompt 17: `LOYALTY_POINTS` ödemesinde harcanan puan (iptal / iade karşı kaydında eksi); diğerlerinde null. */
    points: z
        .string()
        .regex(/^-?\d{1,15}$/)
        .nullable(),
    /** Ödeme düzeltmesiyle (`PosSaleRetender`) geçersiz kılındıysa dolu; satır SİLİNMEZ. */
    supersededAt: timestampSchema.nullable(),
    supersededReason: z.string().nullable(),
});
export const posSaleDetailSchema = posSaleSummarySchema.extend({
    companyId: uuidSchema,
    agentId: uuidSchema.nullable(),
    shiftMemberId: uuidSchema.nullable(),
    approvedByStaffId: uuidSchema.nullable(),
    approvedByStaffName: z.string().nullable(),
    cashRegisterId: uuidSchema.nullable(),
    cashRegisterName: z.string().nullable(),
    voidOfSaleId: uuidSchema.nullable(),
    voidReason: z.string().nullable(),
    /** Bu satışı iptal eden / iade eden kayıt (varsa). */
    voidedBySaleId: uuidSchema.nullable(),
    vatBreakdown: z.array(posSaleVatLineSchema),
    receiptPrintedAt: timestampSchema.nullable(),
    earchiveRequestId: uuidSchema.nullable(),
    ageConfirmedByStaffId: uuidSchema.nullable(),
    ageConfirmedAt: timestampSchema.nullable(),
    /** KVKK: plaka kişisel veri olabilir; yalnızca `pos.read` yetkisiyle döner. */
    vehiclePlate: z.string().nullable(),
    loyaltyCardRef: z.string().nullable(),
    note: z.string().nullable(),
    // ── Prompt 17 (ADR 0017 §6, §14): sunucunun müşteri tanıma sonucu ve ajanın satış anındaki kararları ──
    customerProfileId: uuidSchema.nullable(),
    /** Kasanın bildirdiği tanıma yolu ya da `SERVER` (sunucu ham plaka / telefon / karttan tanıdı). */
    customerIdentifiedBy: z
        .enum(["CARD", "QR", "PLATE", "TAG", "DRIVER_CARD", "PHONE", "MANUAL", "SERVER"])
        .nullable(),
    vehicleId: uuidSchema.nullable(),
    fleetDriverId: uuidSchema.nullable(),
    odometerKm: z.number().int().nullable(),
    /** Kampanya hesabında KULLANILAN seviye (denetim izi). */
    loyaltyTier: z.string().nullable(),
    coupons: z.array(z.object({ couponId: uuidSchema.nullable(), code: z.string(), campaignId: uuidSchema.nullable() })),
    /** Ajanın satış anındaki filo kararı (bilgi); sunucunun yeniden değerlendirmesi `warnings`'tedir. */
    fleetAuthorization: z
        .object({
        verdict: z.string(),
        source: z.string(),
        action: z.string(),
        violations: z.array(z.object({ code: z.string(), message: z.string().nullable().optional() }).loose()),
        warnings: z.array(z.string()),
    })
        .loose()
        .nullable(),
    lines: z.array(posSaleLineSchema),
    tenders: z.array(posSaleTenderSchema),
});
export const listPosSalesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    staffId: uuidSchema.optional(),
    shiftId: uuidSchema.optional(),
    status: posSaleStatusSchema.optional(),
    kind: posSaleKindSchema.optional(),
    businessDateFrom: localDateSchema.optional(),
    businessDateTo: localDateSchema.optional(),
    /** Yalnızca uyarı taşıyan ya da gün kilidinden sonra düşen satışlar. */
    flaggedOnly: z.enum(["true", "false"]).optional(),
});
export const posSaleParamsSchema = z.object({ id: uuidSchema });
// ── Rapor ────────────────────────────────────────────────────────────────────
export const posReportTenderTotalSchema = z.object({
    tenderType: posTenderTypeSchema,
    amount: moneySchema.describe("Kasada kalan tutar (nakitte para üstü düşülmüş); iptal edilen satışın ödemesi karşı kaydıyla sıfırlanır"),
    count: z.number().int().nonnegative(),
});
export const posReportRowSchema = z.object({
    businessDate: localDateSchema,
    stationId: uuidSchema,
    stationName: z.string().nullable(),
    staffId: uuidSchema.nullable(),
    staffName: z.string().nullable().describe("KVKK: personel adı"),
    saleCount: z.number().int().nonnegative(),
    returnCount: z.number().int().nonnegative(),
    voidCount: z.number().int().nonnegative(),
    gross: moneySchema,
    discountTotal: moneySchema,
    net: moneySchema,
    vatTotal: moneySchema,
    returnNet: moneySchema.describe("Kısmi iade kayıtlarının (negatif) net toplamı; tam iptalin karşı kaydı iade sayılmaz (voidCount)"),
    tenders: z.array(posReportTenderTotalSchema),
});
export const posReportSchema = z.object({
    companyId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
    generatedAt: timestampSchema,
    rows: z.array(posReportRowSchema),
    totals: z.object({
        saleCount: z.number().int().nonnegative(),
        returnCount: z.number().int().nonnegative(),
        voidCount: z.number().int().nonnegative(),
        gross: moneySchema,
        discountTotal: moneySchema,
        net: moneySchema,
        vatTotal: moneySchema,
        tenders: z.array(posReportTenderTotalSchema),
    }),
});
export const posReportQuerySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    from: localDateSchema,
    to: localDateSchema,
});
// ── Personel PIN'i ───────────────────────────────────────────────────────────
/**
 * DÜZ PIN yalnızca bu istekte taşınır: sunucu argon2id özetini saklar, denetim izine ve günlüğe YAZMAZ.
 * Politika `pos/pin.ts`'te saf fonksiyondur; sunucu ve web aynı kuralı uygular.
 */
export const setStaffPinSchema = z.object({
    pin: z
        .string()
        .regex(new RegExp(`^\\d{${POS_PIN_MIN_LENGTH},${POS_PIN_MAX_LENGTH}}$`), `PIN ${POS_PIN_MIN_LENGTH}–${POS_PIN_MAX_LENGTH} rakam olmalıdır`)
        .describe("KVKK/güvenlik: düz PIN saklanmaz, loglanmaz"),
});
export const staffPinStatusSchema = z.object({
    staffId: uuidSchema,
    stationId: uuidSchema,
    staffCode: z.string(),
    fullName: z.string().describe("KVKK: kişisel veri"),
    hasPin: z.boolean(),
    pinSetAt: timestampSchema.nullable(),
    failedAttempts: z.number().int().nonnegative(),
});
export const staffPinParamsSchema = z.object({ id: uuidSchema });
// ── Ürün üzerindeki POS alanları (katalog modülüne küçük ekleme) ─────────────
export const POS_QUICK_KEY_MIN_SLOT = 1;
export const POS_QUICK_KEY_MAX_SLOT = 48;
export const posQuickKeySlotSchema = z
    .number()
    .int()
    .min(POS_QUICK_KEY_MIN_SLOT)
    .max(POS_QUICK_KEY_MAX_SLOT)
    .describe("Kasa hızlı tuş yuvası (1–48); tekil değildir");
/** Uyarılar `pos_sales.warnings` alanında saklanır; istemci kodu bu kümeden gelir. */
export const posSaleWarningListSchema = z.array(posSaleWarningSchema);
//# sourceMappingURL=sales.js.map